import { ArrowUpRight, Cloud, LockKeyhole, RadioTower, ShieldCheck, Zap } from 'lucide-react';
import { Reveal } from '../../components/motion.tsx';
import { CountUp } from '../../components/CountUp.tsx';
import { HERO_STATS, type EventRow } from './landingData.ts';
import { LandingEventStream } from './LandingEventStream.tsx';

interface Props {
  rows: EventRow[];
  eventCount: number;
  onContact: (subject: string) => void;
}

export function LandingHero({ rows, eventCount, onContact }: Props) {
  return (
    <section className="landing-hero">
      <Reveal>
        <div className="hero-copy">
          <span className="eyebrow">
            <span className="dot" />
            Cloud control for CDVI ATRIUM
          </span>
          <h1 className="display">
            Turn every door into a <span className="serif">command signal</span>.
          </h1>
          <p className="lead">
            CDVI Edge wraps local-only ATRIUM controllers in a modern cloud surface:
            real-time events, tenant-scoped operations, mobile-ready workflows, and
            a relay that does the hard protocol work without exposing your panels.
          </p>

          <div className="cta-row">
            <a href="/handler/sign-in">
              <button className="btn-primary">
                Enter tenant <ArrowUpRight size={16} />
              </button>
            </a>
            <button className="btn-ghost" onClick={() => onContact('CDVI Edge command deck walkthrough')}>
              See the control plane
            </button>
          </div>

          <div className="hero-stat-grid">
            {HERO_STATS.map((stat) => (
              <div className="hero-stat" key={stat.label}>
                <strong>
                  <CountUp value={stat.value} duration={1200} />
                  {stat.suffix}
                </strong>
                <span>{stat.label}</span>
                <small>{stat.note}</small>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.12}>
        <div className="command-deck" aria-label="CDVI Edge command deck preview">
          <div className="deck-topline">
            <span>portfolio mesh</span>
            <strong>4 sites / 182 doors / 99.99%</strong>
          </div>
          <div className="deck-map">
            <div className="site-pin pin-a">
              <span />
              NYC
              <small>84 doors</small>
            </div>
            <div className="site-pin pin-b">
              <span />
              MIA
              <small>48 doors</small>
            </div>
            <div className="site-pin pin-c">
              <span />
              LAX
              <small>31 doors</small>
            </div>
            <div className="relay-node">
              <RadioTower size={22} />
              <span>relay</span>
            </div>
            <div className="cloud-node">
              <Cloud size={22} />
              <span>edge api</span>
            </div>
            <div className="door-command">
              <LockKeyhole size={20} />
              <div>
                <span>Suite 1402</span>
                <strong>mobile key active</strong>
              </div>
            </div>
          </div>

          <LandingEventStream rows={rows} eventCount={eventCount} />

          <div className="deck-actions">
            <span><ShieldCheck size={14} /> tenant scoped</span>
            <span><Zap size={14} /> push alert armed</span>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
